// Şifreli mesajın içindeki yapı (9.32 · D-653).
//
// Bugüne kadar şifrenin içinde yalnız metin vardı. Link önizlemesi metnin
// yanında başlık, logo ve görsel taşımak zorunda; bunlar da şifrenin İÇİNDE
// gitmeli, yoksa sunucu linki görürdü. Bu yüzden önizlemeli mesaj sürümlü bir
// yapıya dönüşüyor.
//
// VARSAYILAN DEĞİŞMEDİ: önizlemesiz mesaj (yani her mesaj, gönderen kısa
// görünümü ya da büyük kartı seçmedikçe) eskisi gibi düz metin. Eski sürümler
// yalnız önizlemeli mesajı ham yapı olarak görür, güncelleyene kadar.
//
// ÖNİZLEME KARŞI TARAFTAN GELİR, yani güvenilmez girdidir. Okurken her alan
// süzülüyor; en önemlisi görseller yalnız mesajın İÇİNDEKİ veri olabilir
// (`data:`). Bir adres kabul edilseydi, alıcının telefonu o adrese bağlanır ve
// IP'sini bir üçüncü tarafa verirdi — tam da bu tasarımın önlediği şey.
import { previewableUrl, splitMessageLinks } from '../utils/links';

export type LinkView = 'compact' | 'card';

export type LinkPreview = {
  /** Metindeki linkin kendisi; dokununca açılan ve alan adının okunduğu adres. */
  url: string;
  view: LinkView;
  title?: string;
  /** Yalnız büyük kartta. */
  description?: string;
  /** `data:image/png|jpeg;base64,…` — sitenin logosu. */
  icon?: string;
  /** `data:image/jpeg;base64,…` — yalnız büyük kartta. */
  image?: string;
};

export type MessagePayload = { text: string; link?: LinkPreview };

export const PAYLOAD_MARKER = 'nosubjectMessage';
const PAYLOAD_VERSION = 1;

// Sınırlar şifreli zarfın tavanından geliyor: bir zarf en çok 64 KB
// (api/src/message-types.ts) ve şifreleme base64 ile metni üçte bir büyütüyor.
// Görsel ~32 KB, logo ~12 KB karakterle toplam ~45 KB düz metin, ~60 KB zarf.
export const PREVIEW_LIMITS = {
  title: 200,
  description: 300,
  iconChars: 12_000,
  imageChars: 32_000,
} as const;

const DATA_IMAGE = /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/]+={0,2}$/;

function cleanText(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  // Kontrol karakterleri ve yön işaretleri başlığı ters çevirip alan adını
  // gizlemek için kullanılabilir; boşluk da tek boşluğa iner.
  const clean = value
    .replace(/[\u0000-\u001f\u007f-\u009f‎‏‪-‮⁦-⁩]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!clean) return undefined;
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}

function cleanImage(value: unknown, maxChars: number): string | undefined {
  if (typeof value !== 'string' || value.length > maxChars) return undefined;
  return DATA_IMAGE.test(value) ? value : undefined;
}

/**
 * Önizlemeyi süz. Metinde aynı adresle duran bir link yoksa önizleme hiç
 * kabul edilmez: alıcının gördüğü alan adı ve dokununca gideceği yer, mesajda
 * gerçekten yazan link olmalı.
 */
export function cleanLinkPreview(raw: unknown, text: string): LinkPreview | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.url !== 'string' || !previewableUrl(o.url)) return null;
  if (o.view !== 'compact' && o.view !== 'card') return null;
  const inText = splitMessageLinks(text).some((s) => s.type === 'link' && s.url === o.url);
  if (!inText) return null;

  const view: LinkView = o.view;
  const link: LinkPreview = { url: o.url, view };
  const title = cleanText(o.title, PREVIEW_LIMITS.title);
  if (title) link.title = title;
  const icon = cleanImage(o.icon, PREVIEW_LIMITS.iconChars);
  if (icon) link.icon = icon;
  if (view === 'card') {
    const description = cleanText(o.description, PREVIEW_LIMITS.description);
    if (description) link.description = description;
    const image = cleanImage(o.image, PREVIEW_LIMITS.imageChars);
    if (image) link.image = image;
  }
  return link;
}

/**
 * Gönderilecek düz metni üret. Önizleme yoksa — varsayılan — metin aynen
 * döner: eski biçim, eski sürümlerle tam uyumlu.
 */
export function encodeMessagePayload(text: string, link?: LinkPreview | null): string {
  const clean = link ? cleanLinkPreview(link, text) : null;
  if (!clean) return text;
  return JSON.stringify({ [PAYLOAD_MARKER]: PAYLOAD_VERSION, text, link: clean });
}

/**
 * Çözülmüş düz metni oku. Yapı değilse metnin kendisidir.
 *
 * İleriye uyumlu: daha yeni bir sürümün yapısı da `text` taşıdığı sürece metin
 * okunur; yalnız anlamadığımız ek alanlar atlanır.
 */
export function decodeMessagePayload(plaintext: string): MessagePayload {
  if (!plaintext.startsWith(`{"${PAYLOAD_MARKER}":`)) return { text: plaintext };
  let raw: unknown;
  try {
    raw = JSON.parse(plaintext);
  } catch {
    return { text: plaintext };
  }
  if (!raw || typeof raw !== 'object') return { text: plaintext };
  const o = raw as Record<string, unknown>;
  if (typeof o[PAYLOAD_MARKER] !== 'number' || typeof o.text !== 'string') return { text: plaintext };
  if (o[PAYLOAD_MARKER] !== PAYLOAD_VERSION) return { text: o.text };
  const link = cleanLinkPreview(o.link, o.text);
  return link ? { text: o.text, link } : { text: o.text };
}
